package fr.manyverse.app.domain.error

/**
 * Base exception class for all domain-level errors.
 * All business logic exceptions should inherit from this class.
 */
sealed class DomainException(message: String, cause: Throwable? = null) : Exception(message, cause)

/**
 * Exception thrown when validation fails in the domain layer.
 * Examples: invalid email format, invalid table code.
 */
open class ValidationError(message: String, cause: Throwable? = null) :
    DomainException(message, cause)

/**
 * Image d'un type accepté que le décodeur de la plateforme n'a pas su lire (fichier corrompu ou
 * mal nommé). L'utilisateur doit en choisir une autre : il reçoit un message traduit, pas
 * [message].
 */
class UnreadableImageError(cause: Throwable? = null) :
    ValidationError("Image could not be decoded", cause)

/**
 * Exception thrown when authentication or authorization fails.
 * Examples: invalid credentials, user not found, session expired.
 */
class AuthenticationError(message: String, cause: Throwable? = null) :
    DomainException(message, cause)

/**
 * Exception thrown when game table operations fail.
 * Examples: table not found, table full, user not member of table.
 */
class TableError(message: String, cause: Throwable? = null) : DomainException(message, cause)

/**
 * Exception thrown when network operations fail.
 * Examples: connection timeout, server unreachable, network unavailable.
 */
class NetworkError(message: String, cause: Throwable? = null) : DomainException(message, cause)

/**
 * Exception thrown when character-related operations fail.
 * Examples: character not found, invalid character data.
 */
class CharacterError(message: String, cause: Throwable? = null) : DomainException(message, cause)

/**
 * Exception thrown when image operations fail.
 * Examples: upload failed, invalid image format, file too large.
 */
class ImageError(message: String, cause: Throwable? = null) : DomainException(message, cause)

/**
 * Exception thrown when audio operations fail.
 * Examples: upload failed, invalid audio format, file too large.
 */
class AudioError(message: String, cause: Throwable? = null) : DomainException(message, cause)

/**
 * Échec technique qu'aucune autre catégorie ne décrit (erreur serveur, refus RLS, réponse
 * illisible). L'utilisateur n'y peut rien : il n'a qu'à réessayer ou signaler l'anomalie.
 */
class UnexpectedError(message: String, cause: Throwable? = null) : DomainException(message, cause)
